import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { compact, keyBy, pickBy } from "lodash";
import { Box, MenuItem, Select, Tooltip } from "@mui/material";
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridRowSelectionModel,
  GridColDef,
  GridRowParams,
  GridActionsCellItem,
  DataGridPro as DataGrid,
  GridLoadingOverlay,
  GridFilterInputValueProps,
  GridFilterItem,
  GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ManageSearch from "@mui/icons-material/ManageSearch";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { pl } from "date-fns/locale/pl";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { useAcceptAbsenceRequests } from "src/Pages/Company/Absences/api/absenceRequests.queries";
import { useGridStatePersistence } from "src/utils/hooks/absences/useGridStatePersistence";
import { useTableOperators } from "src/utils/hooks/absences/useTableOperators";
import { useConfirmAcceptance } from "src/utils/hooks/absences/useConfirmAcceptance";
import { useConfirmRejection } from "src/utils/hooks/absences/useConfirmRejection";
import { useRequireCorrection } from "src/utils/hooks/absences/useRequireCorrection";
import { useConfirmCancellation } from "src/utils/hooks/absences/useConfirmCancellation";
import formatDayLabel from "src/utils/Company/Absences/formatDayLabel";
import formatEmptyValue from "src/utils/Company/Absences/formatEmptyValue";
import Avatar from "src/Components/Avatar";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import GridLinkAction from "src/Pages/Company/Absences/Tables/GridLinkAction";
import DateRangeFilter from "src/Pages/Company/Absences/Tables/DateRangeFliter";
import CustomGridActionsCellItem from "src/Pages/Company/Absences/Tables/CustomGridActionsCellItem";
import CustomToolbar from "src/Pages/Company/Absences/Tables/CustomToolbar";
import {
  EmploymentForm,
  getEmploymentFormLabels,
  getEmploymentFormOptions,
} from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";
import {
  IAbsenceRequestListItem,
  StatusOptions,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { EPatternUnit } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { TableSettings } from "src/Pages/Company/Absences/Tables/tables.models";

interface IAbsenceRequestsTableProps {
  rows: IAbsenceRequestListItem[];
  rowCount: number | undefined;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  showEmployeeData?: boolean;
  loading: boolean;
  storageKey: string;
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  setSortModel: Dispatch<SetStateAction<GridSortModel>>;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
}

interface AbsenceTypeFilterInputProps extends GridFilterInputValueProps {
  item: GridFilterItem;
  applyValue: (filterItem: GridFilterItem) => void;
}

const AbsenceRequestsTable: FC<IAbsenceRequestsTableProps> = ({
  paginationModel,
  rows,
  rowCount,
  sortModel,
  filterModel,
  showEmployeeData = true,
  loading,
  storageKey,
  setPaginationModel,
  setSortModel,
  setFilterModel,
}) => {
  const history = useHistory();
  const basePath = useBasePath();
  const apiRef = useGridApiRef();
  const { getStringOperators, getSelectOperators, getDateOperators } =
    useTableOperators();

  const { initialState } = useGridStatePersistence({
    storageKey,
    apiRef,
  });

  const [settings, setSettings] = useState<TableSettings>({
    density: "standard",
  });

  const [showFilters, setShowFilters] = useState(true);

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set<GridRowId>(),
    });

  const [selectedRowsData, setSelectedRowsData] = useState<
    Map<string | number, IAbsenceRequestListItem>
  >(new Map());

  const { mutate: acceptAbsenceRequests } = useAcceptAbsenceRequests();

  const { confirmRejectionExtended, confirmRejectionMultiple } =
    useConfirmRejection();

  const { confirmAcceptanceMultiple } = useConfirmAcceptance();

  const { requireCorrection } = useRequireCorrection();

  const { confirmCancellation } = useConfirmCancellation();

  const employmentFormLabels = useMemo(() => getEmploymentFormLabels(), []);
  const employmentFormOptions = useMemo(() => getEmploymentFormOptions(), []);

  const columns = useMemo(
    () =>
      [
        ...(showEmployeeData
          ? ([
              {
                headerName: __("Pracownik"),
                field: "employee",
                type: "string",
                width: 80,
                display: "flex",
                valueGetter: (_value, row) => row.employee,
                renderCell: ({ value }) => (
                  <Avatar
                    initials={value.initials}
                    src={value.avatarUrl}
                    employeePresence={value.presenceStatus}
                  />
                ),
                filterable: false,
                sortable: false,
              },
              {
                headerName: __("Imię pracownika"),
                field: "employeeFirstName",
                type: "string",
                width: 180,
                valueGetter: (_value, row) => row.employee.firstName,
                filterOperators: getStringOperators({ labelUT: "Podaj imię" }),
              },
              {
                headerName: __("Nazwisko pracownika"),
                field: "employeeLastName",
                type: "string",
                valueGetter: (_value, row) => row.employee.lastName,
                width: 180,
                filterOperators: getStringOperators({
                  labelUT: "Podaj nazwisko",
                }),
              },
            ] as GridColDef<IAbsenceRequestListItem>[])
          : []),
        {
          headerName: __("Numer kadrowy"),
          field: "employeeFkNumber",
          type: "string",
          valueGetter: (_value, row) => row.employee.fkNumber,
          width: 180,
          filterOperators: getStringOperators({
            labelUT: "Podaj numer kadrowy",
          }),
        },
        {
          headerName: __("Rodzaj nieobecności"),
          field: "absenceTypeName",
          type: "string",
          valueGetter: (_value, row) => row.absenceType.name,
          width: 250,
          filterOperators: [
            {
              headerLabel: __("Podaj rodzaj"),
              value: "contains",
            },
          ],
        },
        {
          headerName: __("Status"),
          field: "status",
          type: "singleSelect",
          valueOptions: StatusOptions.map((option) => ({
            ...option,
            label: __(option.label),
          })),
          renderCell: ({ value }) => <StatusChip variant={value} />,
          width: 200,
          filterOperators: getSelectOperators({ labelUT: "Wybierz status" }),
        },
        {
          headerName: __("Termin wniosku od"),
          field: "start",
          type: "date",
          valueGetter: (_value, row) => new Date(row.range.start.date),
          width: 220,
          filterOperators: getDateOperators({ labelUT: "Wybierz termin od" }),
        },
        {
          headerName: __("Termin wniosku do"),
          field: "end",
          type: "date",
          valueGetter: (_value, row) => new Date(row.range.end.date),
          width: 220,
          filterOperators: getDateOperators({ labelUT: "Wybierz termin do" }),
        },
        {
          headerName: __("Liczba dni/h roboczych nieobecności"),
          field: "effectiveTimeAmount",
          renderCell: ({ row }) => (
            <Box>
              {row.type === EPatternUnit.Day &&
                `${row.effectiveTimeAmount.days} ${__(
                  formatDayLabel(row.effectiveTimeAmount.days)
                )}`}
              {row.type === EPatternUnit.Hour &&
                `${row.effectiveTimeAmount.hours}h`}
            </Box>
          ),
          width: 150,
          sortable: false,
          filterOperators: [
            {
              label: __("Typ nieobecności"),
              value: "absenceType",
              headerLabel: __("Podaj typ"),
              getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) {
                  return undefined;
                }
                return ({ row }) => row.type === filterItem.value;
              },
              InputComponent: ({
                item,
                applyValue,
              }: AbsenceTypeFilterInputProps) => (
                <Select
                  value={item.value}
                  onChange={(e) =>
                    applyValue({ ...item, value: e.target.value })
                  }
                  size="small"
                  fullWidth
                >
                  <MenuItem>{__("Wszystkie")}</MenuItem>
                  <MenuItem value={EPatternUnit.Day}>{__("Dni")}</MenuItem>
                  <MenuItem value={EPatternUnit.Hour}>{__("Godziny")}</MenuItem>
                </Select>
              ),
            },
          ],
        },
        {
          headerName: __("Liczba dni/h kalendarzowych nieobecności"),
          field: "totalTimeAmount",
          renderCell: ({ row }) => (
            <Box>
              {row.type === EPatternUnit.Day &&
                `${row.totalTimeAmount.days} ${__(
                  formatDayLabel(row.totalTimeAmount.days)
                )}`}
              {row.type === EPatternUnit.Hour &&
                `${row.totalTimeAmount.hours}h`}
            </Box>
          ),
          width: 150,
          sortable: false,
          filterOperators: [
            {
              label: __("Typ nieobecności"),
              value: "absenceType",
              headerLabel: __("Podaj typ"),
              getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) {
                  return undefined;
                }
                return ({ row }) => row.type === filterItem.value;
              },
              InputComponent: ({
                item,
                applyValue,
              }: AbsenceTypeFilterInputProps) => (
                <Select
                  value={item.value}
                  onChange={(e) =>
                    applyValue({ ...item, value: e.target.value })
                  }
                  size="small"
                  fullWidth
                >
                  <MenuItem>{__("Wszystkie")}</MenuItem>
                  <MenuItem value={EPatternUnit.Day}>{__("Dni")}</MenuItem>
                  <MenuItem value={EPatternUnit.Hour}>{__("Godziny")}</MenuItem>
                </Select>
              ),
            },
          ],
        },
        {
          headerName: __("Data utworzenia"),
          field: "creationTime",
          type: "date",
          renderHeaderFilter: (params) => <DateRangeFilter {...params} />,
          valueGetter: (value) => (value ? new Date(value) : null),
          width: 240,
        },
        {
          headerName: __("Data akceptacji"),
          field: "acceptationTime",
          type: "date",
          renderHeaderFilter: (params) => <DateRangeFilter {...params} />,
          valueGetter: (value) => (value ? new Date(value) : null),
          renderCell: (params) => (params.value ? params.formattedValue : "-"),
          width: 240,
        },
        {
          headerName: __("Data odrzucenia"),
          field: "rejectionTime",
          type: "date",
          renderHeaderFilter: (params) => <DateRangeFilter {...params} />,
          valueGetter: (value) => (value ? new Date(value) : null),
          renderCell: (params) => (params.value ? params.formattedValue : "-"),
          width: 240,
        },
        {
          headerName: __("Stanowisko"),
          field: "position",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj stanowisko" }),
          valueGetter: (_value, row) => row.employee.position,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Obszar"),
          field: "area",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj obszar" }),
          valueGetter: (_value, row) => row.employee.area,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Wyświetlany dodatkowy obszar (dział)"),
          field: "additionalDisplayArea",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj obszar" }),
          valueGetter: (_value, row) => row.employee.additionalDisplayArea,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Forma zatrudnienia"),
          field: "employmentType",
          type: "singleSelect",
          width: 200,
          valueGetter: (_value, row) => {
            const {
              employee: { employmentType },
            } = row;
            return employmentType &&
              Object.values(EmploymentForm).includes(
                employmentType as EmploymentForm
              )
              ? employmentType
              : null;
          },
          valueFormatter: (value) => {
            if (value === null) return __("-");
            return value in EmploymentForm
              ? employmentFormLabels[value as EmploymentForm]
              : __("Nieznana forma zatrudnienia");
          },
          filterOperators: [
            {
              label: __("równe"),
              value: "equals",
              getApplyFilterFn: (filterItem) => (params) =>
                filterItem.value === "" || params.value === filterItem.value,
              InputComponent: ({
                item,
                applyValue,
              }: GridFilterInputValueProps) => (
                <Select
                  value={item.value}
                  onChange={(e) =>
                    applyValue({ ...item, value: e.target.value })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                >
                  <MenuItem value="">{__("Wszystkie")}</MenuItem>
                  {employmentFormOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              ),
            },
          ],
        },
        {
          headerName: __("Imię wyznaczonego akceptanta"),
          field: "approverFirstName",
          type: "string",
          width: 300,
          filterOperators: getStringOperators({ labelUT: "Podaj imię" }),
          valueGetter: (_value, row) => row.approver?.firstName,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Nazwisko wyznaczonego akceptanta"),
          field: "approverLastName",
          type: "string",
          width: 300,
          filterOperators: getStringOperators({ labelUT: "Podaj nazwisko" }),
          valueGetter: (_value, row) => row.approver?.lastName,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Imię akceptującego"),
          field: "approvedByFirstName",
          type: "string",
          width: 180,
          filterOperators: getStringOperators({ labelUT: "Podaj imię" }),
          valueGetter: (_value, row) => row.approvedBy?.firstName,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Nazwisko akceptującego"),
          field: "approvedByLastName",
          type: "string",
          width: 250,
          filterOperators: getStringOperators({ labelUT: "Podaj nazwisko" }),
          valueGetter: (_value, row) => row.approvedBy?.lastName,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Akcje"),
          field: "actions",
          type: "actions",
          width: 200,
          align: "right",
          getActions: (params: GridRowParams) => {
            const {
              absenceRequestId,
              absenceType: { name },
              absenceTimes,
              employee,
              status,
              eTag,
            } = params.row as IAbsenceRequestListItem;

            const previewLink = `${basePath}/absences/absence-request/${params.row.absenceRequestId}/preview`;

            const actions = [
              {
                type: "link",
                key: "preview",
                icon: <ManageSearch />,
                label: __("Podgląd"),
                bgColor: "info.main",
                bgColorHover: "info.dark",
                to: previewLink,
                showIf: () => true,
              },
              {
                type: "default",
                key: "accept",
                icon: <CheckIcon />,
                showIf: (row: IAbsenceRequestListItem) =>
                  row.actionsAllowed.approvable,
                label: __("Akceptuj"),
                onClick: () =>
                  acceptAbsenceRequests({
                    absenceRequests: [absenceRequestId!],
                    absenceRequestsWithETags: [
                      {
                        absenceRequestId,
                        eTag,
                      },
                    ],
                  }),
                sx: {
                  color: "white",
                  backgroundColor: "success.main",
                  "&:hover": {
                    backgroundColor: "success.dark",
                  },
                },
              },
              {
                type: "default",
                key: "reject",
                icon: <ClearIcon />,
                label: __("Odrzuć"),
                showIf: (row: IAbsenceRequestListItem) =>
                  row.actionsAllowed.rejectable,
                onClick: () =>
                  confirmRejectionExtended({
                    absenceRequestId,
                    absenceTypeName: name,
                    employee,
                    absenceTimes,
                    status,
                    previewLink,
                    eTag,
                  }),
                sx: {
                  color: "white",
                  backgroundColor: "error.main",
                  "&:hover": {
                    backgroundColor: "error.dark",
                  },
                },
              },
              {
                type: "custom",
                key: "edit",
                icon: <EditNoteIcon />,
                label: __("Edytuj"),
                bgColor: "info.main",
                bgColorHover: "info.dark",
                onClick: () => {
                  history.push(
                    `${basePath}/absences/absence-request/${params.row.absenceRequestId}/edit/${employee.employeeId}`
                  );
                },
                showIf: (row: IAbsenceRequestListItem) =>
                  row.actionsAllowed.editable,
                showInMenu: true,
              },
              {
                type: "custom",
                key: "correction",
                icon: <WarningAmberOutlinedIcon />,
                showIf: (row: IAbsenceRequestListItem) =>
                  row.actionsAllowed.sendForCorrectionEligible,
                label: __("Zwróć do poprawy"),
                showInMenu: true,
                bgColor: "warning.main",
                bgColorHover: "warning.dark",
                onClick: () => requireCorrection(absenceRequestId, eTag),
              },
              {
                type: "custom",
                key: "cancel",
                icon: <DeleteOutline />,
                showIf: (row: IAbsenceRequestListItem) =>
                  row.actionsAllowed.cancelable,
                label: __("Anuluj"),
                showInMenu: true,
                bgColor: "error.main",
                bgColorHover: "error.dark",
                onClick: () =>
                  confirmCancellation(absenceRequestId, name, eTag),
              },
            ];

            return actions
              .filter((actionItem) => actionItem.showIf(params.row))
              .map((actionItem) => {
                const { key, icon, label, onClick, sx, showInMenu } =
                  actionItem;

                if (actionItem.type === "link") {
                  const { bgColor, bgColorHover, to } = actionItem;

                  return (
                    <GridLinkAction
                      key={key}
                      icon={icon}
                      label={label}
                      bgColor={bgColor || "info.main"}
                      bgColorHover={bgColorHover || "info.dark"}
                      to={to || previewLink}
                      dataTestId={`absence-request-action-${key}`}
                    />
                  );
                }

                if (actionItem.type === "custom") {
                  const { bgColor, bgColorHover } = actionItem;

                  return (
                    <CustomGridActionsCellItem
                      key={key}
                      icon={icon}
                      label={label}
                      bgColor={bgColor || "info.main"}
                      bgColorHover={bgColorHover || "info.dark"}
                      showInMenu={showInMenu}
                      onClick={onClick}
                      dataTestId={`absence-request-action-${key}`}
                    />
                  );
                }

                return (
                  <Tooltip title={label} placement="top" arrow>
                    <GridActionsCellItem
                      key={key}
                      icon={icon}
                      label={label}
                      onClick={onClick}
                      material={{
                        sx,
                      }}
                      data-testId={`absence-request-action-${key}`}
                    />
                  </Tooltip>
                );
              });
          },
        },
      ] as GridColDef<IAbsenceRequestListItem>[],
    [
      getStringOperators,
      getSelectOperators,
      getDateOperators,
      employmentFormLabels,
      employmentFormOptions,
      basePath,
      acceptAbsenceRequests,
      confirmRejectionExtended,
      history,
      requireCorrection,
      confirmCancellation,
      showEmployeeData,
    ]
  );

  const handleRowSelectionChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newRowSelectionModel);

      setSelectedRowsData((prevData) => {
        const rowsById = keyBy(rows, "absenceRequestId");

        const selectedIds = Array.from(newRowSelectionModel.ids);

        const selectedRowsFromCurrentPage = compact(
          selectedIds.map((id) => rowsById[id])
        );

        const previouslySelectedStillSelected = pickBy(
          Object.fromEntries(prevData),
          (_, id) => newRowSelectionModel.ids.has(Number(id) || id)
        );

        const combinedRows = {
          ...previouslySelectedStillSelected,
          ...keyBy(selectedRowsFromCurrentPage, "absenceRequestId"),
        };

        return new Map(Object.entries(combinedRows));
      });
    },
    [rows]
  );

  const resetRowSelection = useCallback(() => {
    setRowSelectionModel({
      type: "include",
      ids: new Set<GridRowId>(),
    });
    setSelectedRowsData(new Map());
  }, []);

  const onBatchRejection = useCallback(() => {
    const chosenAbsenceRequests = Array.from(selectedRowsData.values());

    confirmRejectionMultiple(
      chosenAbsenceRequests,
      rowCount || 0,
      resetRowSelection
    );
  }, [confirmRejectionMultiple, resetRowSelection, rowCount, selectedRowsData]);

  const onBatchAcceptance = useCallback(() => {
    const chosenAbsenceRequests = Array.from(selectedRowsData.values());

    confirmAcceptanceMultiple(
      chosenAbsenceRequests,
      rowCount || 0,
      resetRowSelection
    );
  }, [
    confirmAcceptanceMultiple,
    resetRowSelection,
    rowCount,
    selectedRowsData,
  ]);

  if (!initialState) {
    return null;
  }

  return (
    <Box
      sx={{ height: 700, width: "100%" }}
      data-testid="absence-request-table"
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
        <DataGrid
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.absenceRequestId}
          rowCount={rowCount}
          loading={loading}
          initialState={initialState}
          pinnedColumns={{ left: [], right: ["actions"] }}
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          disableEval
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          density={settings.density}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableColumnFilter
          headerFilters={showFilters}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          filterDebounceMs={500}
          showToolbar
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleRowSelectionChange}
          isRowSelectable={(params: GridRowParams) =>
            params.row.actionsAllowed.rejectable ||
            params.row.actionsAllowed.approvable
          }
          slots={{
            headerFilterMenu: null,
            toolbar: () => (
              <CustomToolbar
                rowSelectionModel={rowSelectionModel}
                settings={settings}
                setSettings={setSettings}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onBatchRejection={onBatchRejection}
                onBatchAcceptance={onBatchAcceptance}
              />
            ),
            loadingOverlay: () => (
              <GridLoadingOverlay
                variant="linear-progress"
                noRowsVariant="skeleton"
              />
            ),
          }}
          slotProps={{
            toolbar: { printOptions: { disableToolbarButton: true } },
            row: {
              "data-testid": "absence-requests-table__row",
            },
            cell: {
              "data-testid": "absence-requests-table__cell",
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default AbsenceRequestsTable;
